<template>
  <Teleport to="body">
    <motion.div
      v-if="modelValue"
      class="account-blocked-overlay"
      :initial="{ opacity: 0 }"
      :animate="{ opacity: 1 }"
      @click.self="close"
    >
      <motion.div
        class="account-blocked-card"
        :initial="{ opacity: 0, scale: 0.92, y: 24 }"
        :animate="{ opacity: 1, scale: 1, y: 0 }"
        :transition="{ type: 'spring', stiffness: 320, damping: 28 }"
      >
        <div class="account-blocked-glow" aria-hidden="true" />
        <div class="account-blocked-icon-wrap">
          <svg class="account-blocked-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
          </svg>
        </div>
        <h2 class="account-blocked-title">{{ t('accountBlocked.modal.title') }}</h2>
        <p class="account-blocked-desc">{{ t('accountBlocked.modal.description') }}</p>
        <p class="account-blocked-hint">{{ t('accountBlocked.modal.hint') }}</p>
        <motion.button
          type="button"
          class="account-blocked-cta"
          :whileHover="{ scale: 1.02 }"
          :whileTap="{ scale: 0.98 }"
          @click="goSupport"
        >
          {{ t('accountBlocked.supportCta') }}
        </motion.button>
        <button type="button" class="account-blocked-dismiss" @click="close">
          {{ t('accountBlocked.dismiss') }}
        </button>
      </motion.div>
    </motion.div>
  </Teleport>
</template>

<script setup>
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { motion } from 'motion-v'

const { t } = useI18n()

defineProps({
  modelValue: { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue'])
const router = useRouter()

const close = () => emit('update:modelValue', false)

const goSupport = async () => {
  close()
  await router.push('/suporte')
}
</script>

<style scoped>
.account-blocked-overlay {
  position: fixed;
  inset: 0;
  z-index: 10060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: color-mix(in srgb, #0f172a 55%, transparent);
  backdrop-filter: blur(8px);
}

.account-blocked-card {
  position: relative;
  width: min(100%, 420px);
  padding: 32px 28px 24px;
  border-radius: 20px;
  border: 1px solid color-mix(in srgb, #ef4444 25%, var(--border-primary));
  background: var(--bg-primary);
  text-align: center;
  overflow: hidden;
  box-shadow:
    0 24px 48px color-mix(in srgb, #000 28%, transparent),
    0 0 0 1px color-mix(in srgb, #fff 6%, transparent) inset;
}

.account-blocked-glow {
  position: absolute;
  inset: -40% -20% auto;
  height: 180px;
  background: radial-gradient(ellipse, color-mix(in srgb, #ef4444 18%, transparent), transparent 70%);
  pointer-events: none;
}

.account-blocked-icon-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-bottom: 16px;
  border-radius: 16px;
  color: #ef4444;
  background: color-mix(in srgb, #ef4444 12%, transparent);
  border: 1px solid color-mix(in srgb, #ef4444 22%, transparent);
}

.account-blocked-icon {
  width: 28px;
  height: 28px;
}

.account-blocked-title {
  position: relative;
  margin: 0 0 10px;
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--text-primary);
}

.account-blocked-desc {
  position: relative;
  margin: 0 0 8px;
  font-size: 0.9375rem;
  line-height: 1.55;
  color: var(--text-secondary);
}

.account-blocked-hint {
  position: relative;
  margin: 0 0 22px;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: var(--text-tertiary, var(--text-secondary));
}

.account-blocked-cta {
  position: relative;
  width: 100%;
  padding: 12px 20px;
  border: none;
  border-radius: 12px;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  background: linear-gradient(135deg, #6366f1, #7c3aed);
  box-shadow: 0 8px 20px color-mix(in srgb, #6366f1 32%, transparent);
}

.account-blocked-dismiss {
  margin-top: 12px;
  padding: 8px 12px;
  border: none;
  background: none;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-secondary);
  cursor: pointer;
}

.account-blocked-dismiss:hover {
  color: var(--text-primary);
}
</style>
